import { createSlice } from "@reduxjs/toolkit";
import store, { StoreDispatch } from "../store";
import { immediateToast } from "izitoast-react";
import {
  getActivity,
  getActivityRequest,
  getAllTransaction,
  createTransactionAllocation,
  updateTransactionAllocation,
  CreateTransactionAllocationRowInput,
  UpdateTransactionAllocationRequest,
  TransactionAllocation,
  getTransactionAllocations,
  getAccountTransaction,
  updateAccountTransaction,
  createAccountingTransaction,
  getAccountingAudit,
  UpdateAccountTransactionRequest
} from "src/network/graphql/transactionService";
import { getWalletDetail } from "src/network/graphql/walletService";
import { resolveUserNamesAction } from "src/page/userNamesSlice";
import { Transaction } from "../transactions/transactionSlice";
import { EMPTY_STRING } from "src/components/constant/constant";
import { AccountStatus } from "src/types/commonTypes";
import type { WalletDetailData as WalletDetail } from "../walletDetail/walletDetailSlice";

export type AccountingTransaction = {
  accounting_transaction_id: string;
  activity_id: string;
  status: AccountStatus;
  tax_details: {
    gst_amount: number;
  };
  xero_bankTransaction: {
    synced_at: string;
  };
};

export type AccountingAuditItem = {
  status: string;
  updated_at: string;
  updated_by: string;
  reason?: string;
};
export type TransactionWalletDetail = WalletDetail["wallet"]["preferences"];

const initialState = {
  loading: false,
  error: null,
  transactionDetail: {} as Transaction,
  allocationsLoading: false,
  transactionAllocations: [] as TransactionAllocation[],
  accountingTransactionLoading: false,
  accountingTransaction: {} as AccountingTransaction,
  walletDetail: {} as TransactionWalletDetail,
  accountingAudit: [] as AccountingAuditItem[],
  accountingAuditLoading: false,
  currentActivityId: null as string | null,
  relatedTransactions: [] as Transaction[],
  relatedTransactionsLoading: false,
  relatedTransactionsSource: null as "referenced" | "referencing" | null
};

const transactionDetailSlice = createSlice({
  name: "transactionDetail",
  initialState,
  reducers: {
    fetchTransactionDetailStart: (state) => {
      state.loading = true;
    },
    fetchTransactionDetailSuccess: (state, action) => {
      state.loading = false;
      state.transactionDetail = action.payload ?? {};
    },
    fetchTransactionDetailFail: (state, action) => {
      state.loading = false;
      state.transactionDetail = {} as Transaction;
      state.error = action.payload;
    },
    fetchTransactionAllocationsStart: (state) => {
      state.allocationsLoading = true;
    },
    fetchTransactionAllocationsSuccess: (state, action) => {
      state.allocationsLoading = false;
      state.transactionAllocations = action.payload ?? [];
    },
    fetchTransactionAllocationsFail: (state, action) => {
      state.allocationsLoading = false;
      state.transactionAllocations = [];
      state.error = action.payload;
    },
    createTransactionAllocationsStart: (state) => {
      state.allocationsLoading = true;
    },
    createTransactionAllocationsSuccess: (state) => {
      state.allocationsLoading = false;
    },
    createTransactionAllocationsFail: (state, action) => {
      state.allocationsLoading = false;
      state.error = action.payload;
    },
    updateTransactionAllocationsStart: (state) => {
      state.allocationsLoading = true;
    },
    updateTransactionAllocationsSuccess: (state) => {
      state.allocationsLoading = false;
    },
    updateTransactionAllocationsFail: (state, action) => {
      state.allocationsLoading = false;
      state.error = action.payload;
    },
    fetchAccountTransactionStart: (state) => {
      state.accountingTransactionLoading = true;
    },
    fetchAccountTransactionSuccess: (state, action) => {
      state.accountingTransactionLoading = false;
      state.accountingTransaction = action.payload ?? null;
    },
    fetchAccountTransactionFail: (state, action) => {
      state.accountingTransactionLoading = false;
      state.accountingTransaction = {} as AccountingTransaction;
      state.error = action.payload;
    },
    updateAccountTransactionStart: (state) => {
      state.accountingTransactionLoading = true;
    },
    updateAccountTransactionSuccess: (state, action) => {
      state.accountingTransactionLoading = false;
      if (state.accountingTransaction) {
        if (action.payload?.status) {
          state.accountingTransaction.status = action.payload.status;
        }
        if (action.payload?.tax_details) {
          state.accountingTransaction.tax_details = action.payload.tax_details;
        }
      }
    },
    updateAccountTransactionFail: (state, action) => {
      state.accountingTransactionLoading = false;
      state.error = action.payload;
    },
    fetchWalletDetailStart: (state) => {
      state.walletDetail = {} as TransactionWalletDetail;
    },
    fetchWalletDetailSuccess: (state, action) => {
      state.walletDetail = action.payload;
    },
    fetchWalletDetailFail: (state) => {
      state.walletDetail = {} as TransactionWalletDetail;
    },
    fetchAccountingAuditStart: (state) => {
      state.accountingAuditLoading = true;
    },
    fetchAccountingAuditSuccess: (state, action) => {
      state.accountingAuditLoading = false;
      state.accountingAudit = action.payload;
    },
    fetchAccountingAuditFail: (state) => {
      state.accountingAuditLoading = false;
      state.accountingAudit = [];
    },
    resetTransactionDetailState: () => ({ ...initialState }),
    setCurrentActivityId: (state, action: { payload: string | null }) => {
      state.currentActivityId = action.payload;
    },
    fetchRelatedTransactionsStart: (state) => {
      state.relatedTransactionsLoading = true;
      state.relatedTransactions = [];
      state.relatedTransactionsSource = null;
    },
    fetchRelatedTransactionsSuccess: (state, action) => {
      state.relatedTransactionsLoading = false;
      state.relatedTransactions = action.payload?.list ?? [];
      state.relatedTransactionsSource = action.payload?.source ?? null;
    },
    fetchRelatedTransactionsFail: (state, action) => {
      state.relatedTransactionsLoading = false;
      state.relatedTransactions = [];
      state.relatedTransactionsSource = null;
      state.error = action.payload;
    }
  }
});

const {
  fetchTransactionDetailStart,
  fetchTransactionDetailSuccess,
  fetchTransactionDetailFail,
  fetchTransactionAllocationsStart,
  fetchTransactionAllocationsSuccess,
  fetchTransactionAllocationsFail,
  createTransactionAllocationsStart,
  createTransactionAllocationsSuccess,
  createTransactionAllocationsFail,
  updateTransactionAllocationsStart,
  updateTransactionAllocationsSuccess,
  updateTransactionAllocationsFail,
  fetchAccountTransactionStart,
  fetchAccountTransactionSuccess,
  fetchAccountTransactionFail,
  updateAccountTransactionStart,
  updateAccountTransactionSuccess,
  updateAccountTransactionFail,
  fetchWalletDetailStart,
  fetchWalletDetailSuccess,
  fetchWalletDetailFail,
  fetchAccountingAuditStart,
  fetchAccountingAuditSuccess,
  fetchAccountingAuditFail,
  resetTransactionDetailState,
  setCurrentActivityId,
  fetchRelatedTransactionsStart,
  fetchRelatedTransactionsSuccess,
  fetchRelatedTransactionsFail
} = transactionDetailSlice.actions;

export { resetTransactionDetailState };

const isRequestStale = (activityId: string) => store.getState().transactionDetail.currentActivityId !== activityId;

export const fetchTransactionDetailAction = (data: getActivityRequest) => {
  return async (dispatch: StoreDispatch) => {
    const activityId = data.activity_id;

    if (!activityId) return;
    dispatch(fetchTransactionDetailStart());
    try {
      const response = await getActivity(data);

      if (isRequestStale(activityId)) return;

      const transaction = {
        id: response.data.get_activity.activity_id,
        name: response.data.get_activity.activity_name,
        endDate: response.data.get_activity.end_timestamp,
        startDate: response.data.get_activity.start_timestamp,
        ownerId: response.data.get_activity.owner_id,
        activityStatus: response.data.get_activity.activity_status,
        receipt_available: response.data.get_activity.receipt_available,
        approval_number: response.data.get_activity.approval_number,
        data: JSON.parse(response.data.get_activity?.data || "{}"),
        verified: response.data.get_activity?.attributes?.verified,
        tagged_users: response.data.get_activity?.attributes?.tagged_users,
        expense_id: response.data.get_activity?.attributes?.expense_id
      };

      dispatch(fetchTransactionDetailSuccess(transaction));
    } catch (error) {
      if (isRequestStale(activityId)) return;
      dispatch(fetchTransactionDetailFail(error));
    }
  };
};

export const fetchAccountTransactionAction = (activityId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchAccountTransactionStart());
    try {
      const response = await getAccountTransaction(activityId);

      if (isRequestStale(activityId)) return EMPTY_STRING;

      let data = response.data?.get_accounting_transaction;

      if (!data?.accounting_transaction_id) {
        await createAccountingTransaction(activityId);

        if (isRequestStale(activityId)) return EMPTY_STRING;

        const refetchResponse = await getAccountTransaction(activityId);

        if (isRequestStale(activityId)) return EMPTY_STRING;

        data = refetchResponse.data?.get_accounting_transaction ?? undefined;
      }

      const payload = data
        ? {
            accounting_transaction_id: data.accounting_transaction_id,
            activity_id: data.activity_id,
            status: (data.status || "DRAFT") as AccountStatus,
            tax_details: data.tax_details,
            xero_bankTransaction: data.xero_bankTransaction
          }
        : null;

      if (isRequestStale(activityId)) return EMPTY_STRING;
      dispatch(fetchAccountTransactionSuccess(payload));

      return payload?.accounting_transaction_id;
    } catch (error) {
      if (isRequestStale(activityId)) return EMPTY_STRING;
      dispatch(fetchAccountTransactionFail(error));
      return EMPTY_STRING;
    }
  };
};

export const updateAccountTransactionAction = (data: UpdateAccountTransactionRequest, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateAccountTransactionStart());
    try {
      await updateAccountTransaction(data);
      dispatch(updateAccountTransactionSuccess({ status: data.status, tax_details: data.tax_details }));
      immediateToast("success", {
        message: "Transaction status updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateAccountTransactionFail(error));
    }
  };
};

export const fetchWalletDetailAction = (walletId: string, activityId?: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletDetailStart());
    try {
      // Optional context (card limits) — get_wallet is authorizer-denied for
      // roles without a user↔wallet edge (CPC has none), e.g. a Team Member
      // approver viewing a cardholder's transaction. Never let that deny toast
      // or bounce the session; the page works without it (QA OBS-QB1).
      const response = await getWalletDetail(walletId, { suppressAuthError: true });

      if (activityId && isRequestStale(activityId)) return;

      const preferences = response.data?.get_wallet?.preferences;

      dispatch(
        fetchWalletDetailSuccess({
          cashWithdrawalAllowed: preferences?.is_cash_withdrawal_allowed ?? false,
          maxAllowedLimit: preferences?.maximum_allowed_limit ?? 0,
          preferenceId: preferences?.preference_id ?? EMPTY_STRING,
          spendFrequency: preferences?.spend_frequency ?? "WEEKLY",
          limit_resets_at: preferences?.limit_resets_at ?? "1",
          spendLimit: preferences?.spend_limit ?? 0,
          blockedMerchants: preferences?.blocked_merchant_ids ?? [],
          blockedMccCategories: preferences?.blocked_mcc_categories ?? [],
          approvers: preferences?.approvers ?? [],
          verifiers: preferences?.verifiers ?? [],
          isTransferAllowed: preferences?.is_transfer_allowed ?? true,
          expenses_since_last_reset: preferences?.expenses_since_last_reset ?? 0,
          next_reset_date: preferences?.next_reset_date ?? EMPTY_STRING,
          xero_account_id: preferences?.xero_account_id ?? EMPTY_STRING,
          xero_tax_type: preferences?.xero_tax_type ?? EMPTY_STRING,
          xero_tracking_category_mapping: preferences?.xero_tracking_category_mapping ?? []
        })
      );
    } catch {
      if (activityId && isRequestStale(activityId)) return;
      dispatch(fetchWalletDetailFail());
    }
  };
};

export const fetchAccountingAuditAction = (accountingTransactionId: string, activityId?: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchAccountingAuditStart());
    try {
      const response = await getAccountingAudit(accountingTransactionId);

      if (activityId && isRequestStale(activityId)) return;
      const rawAudit = response.data?.get_accounting_audit ?? [];
      const parsed: AccountingAuditItem[] = [];
      const userIdsToResolve: string[] = [];

      for (const item of rawAudit) {
        const event = typeof item?.event === "string" ? JSON.parse(item.event) : item?.event;
        const updatedBy = event?.updated_by ?? "SYSTEM";
        const reason = event?.rejection_context?.reason ?? event?.reason;

        parsed.push({
          status: event?.status,
          updated_at: event?.updated_at,
          updated_by: updatedBy,
          ...(reason && { reason })
        });

        if (updatedBy && updatedBy !== "SYSTEM") {
          userIdsToResolve.push(updatedBy);
        }
      }

      if (activityId && isRequestStale(activityId)) return;
      dispatch(fetchAccountingAuditSuccess(parsed));

      if (userIdsToResolve.length > 0) {
        dispatch(resolveUserNamesAction(userIdsToResolve));
      }
    } catch {
      if (activityId && isRequestStale(activityId)) return;
      dispatch(fetchAccountingAuditFail());
    }
  };
};

export const fetchTransactionAllocationsAction = (activityId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransactionAllocationsStart());

    try {
      const response = await getTransactionAllocations(activityId);

      if (isRequestStale(activityId)) return;

      dispatch(fetchTransactionAllocationsSuccess(response.data?.get_transaction_allocations ?? []));
    } catch (error) {
      if (isRequestStale(activityId)) return;
      dispatch(fetchTransactionAllocationsFail(error));
    }
  };
};

export const createTransactionAllocationsAction = (
  activityId: string,
  rows: CreateTransactionAllocationRowInput[],
  onSuccess?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createTransactionAllocationsStart());
    try {
      const promises = rows.map((row) =>
        createTransactionAllocation({
          activity_id: activityId,
          ...row
        })
      );

      await Promise.all(promises);
      dispatch(createTransactionAllocationsSuccess());
      immediateToast("success", {
        message: "Allocation created successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(createTransactionAllocationsFail(error));
    }
  };
};

export const updateTransactionAllocationsAction = (
  activityId: string,
  updateRows: UpdateTransactionAllocationRequest[],
  createRows: CreateTransactionAllocationRowInput[],
  onSuccess?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateTransactionAllocationsStart());
    try {
      const updatePromises = updateRows.map((row) => updateTransactionAllocation(row));
      const createPromises = createRows.map((row) =>
        createTransactionAllocation({
          activity_id: activityId,
          ...row
        })
      );

      await Promise.all([...updatePromises, ...createPromises]);
      dispatch(updateTransactionAllocationsSuccess());
      immediateToast("success", {
        message: "Allocation updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(updateTransactionAllocationsFail(error));
    }
  };
};

const mapActivityToTransaction = (activity: any) => ({
  id: activity.activity_id,
  name: activity.activity_name,
  Processstatus: activity.process_status ?? "",
  endDate: activity.end_timestamp,
  startDate: activity.start_timestamp ?? activity.created_at,
  ownerId: activity.owner_id,
  activityStatus: activity.activity_status,
  receipt_available: activity.receipt_available,
  approval_number: activity.approval_number,
  data: JSON.parse(activity.data),
  xero_details: activity?.xero_details,
  verified: activity?.attributes?.verified,
  tagged_users: activity?.attributes?.tagged_users,
  expense_id: activity?.attributes?.expense_id
});

export const fetchRelatedTransactionsAction = (activityId: string, referenceActivityId?: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchRelatedTransactionsStart());
    try {
      if (referenceActivityId) {
        const activityResponse = await getActivity({ activity_id: referenceActivityId });

        if (isRequestStale(activityId)) return;

        const activity = activityResponse.data?.get_activity;

        if (!activity) {
          dispatch(fetchRelatedTransactionsSuccess([]));
          return;
        }

        const relatedList = [mapActivityToTransaction(activity)];

        dispatch(
          fetchRelatedTransactionsSuccess({
            list: relatedList,
            source: "referenced"
          })
        );
        return;
      }

      const searchResponse = await getAllTransaction({
        from: 0,
        size: 100,
        filter: { reference_activity_id: activityId }
      });

      if (isRequestStale(activityId)) return;

      const listing = searchResponse.data?.search_activity?.activity_listing ?? [];

      const list = listing.map((item: any) => mapActivityToTransaction(item));

      dispatch(
        fetchRelatedTransactionsSuccess({
          list,
          source: "referencing"
        })
      );
    } catch (error) {
      if (isRequestStale(activityId)) return;
      dispatch(fetchRelatedTransactionsFail(error));
    }
  };
};

export const loadTransactionDetailPageAction = (activityId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setCurrentActivityId(activityId));
    dispatch(resetTransactionDetailState());
    dispatch(setCurrentActivityId(activityId));

    await Promise.all([
      dispatch(fetchTransactionDetailAction({ activity_id: activityId })),
      dispatch(fetchAccountTransactionAction(activityId)),
      dispatch(fetchTransactionAllocationsAction(activityId))
    ]);

    if (isRequestStale(activityId)) return;

    const state = store.getState().transactionDetail;
    const transactionData = state.transactionDetail?.data as { reference_activity_id?: string } | undefined;
    const referenceActivityId = transactionData?.reference_activity_id;

    dispatch(fetchRelatedTransactionsAction(activityId, referenceActivityId));

    if (isRequestStale(activityId)) return;

    const stateAfter = store.getState().transactionDetail;
    const accountingId = stateAfter.accountingTransaction?.accounting_transaction_id;
    const walletId = stateAfter.transactionDetail?.data?.wallet?.wallet_id;

    if (accountingId) {
      dispatch(fetchAccountingAuditAction(accountingId, activityId));
    }

    if (walletId) {
      dispatch(fetchWalletDetailAction(walletId, activityId));
    }
  };
};

export default transactionDetailSlice.reducer;
